import { Col, Space, Form, Row, Table, Input, Button } from 'antd';
import { t } from 'i18next';
import {
  ChangeEventHandler,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { useUIDispatchContext } from 'providers/UI';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import {
  useOrganizationsDispatchContext,
  useOrganizationsStateContext
} from 'providers/Organizations';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import { addOrganizationsInfo } from 'store/analytics/slice';
import {
  deleteOrganization,
  fetchOrganizations
} from 'services/api/organizations';
import { OrganizationListRow } from 'types/api/organizations';

import { ActionMenu, PageWrapper } from 'components/ui';
import { OrganizationsModal } from './components';
import { showErrorMessage } from 'utils/showErrorMessage';
import { Private } from 'components/shared';

const Organizations: FC = () => {
  const [isDetailsVisible, setDetailsVisible] = useState(false);
  const [detailsId, setDetailsId] = useState('');
  const [search, setSearch] = useState('');
  const idToDelete = useRef('');
  const timer = useRef<NodeJS.Timeout | null>(null);
  const { toggleModal } = useUIDispatchContext();
  const { results: organizations, selected } = useOrganizationsStateContext();
  const { onChangeParent } = useOrganizationsDispatchContext();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [rows, setRows] = useState<OrganizationListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchOrganizations();
        if (!cancelled) setRows(res);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useEffect(() => {
    if (rows.length) {
      dispatch(addOrganizationsInfo(rows));
    }
  }, [rows, dispatch]);

  const results = rows.filter(item => {
    // Org name is jsonb Record<locale, string> — match across locales.
    const haystack = Object.values(item.name || {})
      .join(' ')
      .toLowerCase();

    return haystack.includes(search.toLowerCase());
  });

  const onSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        setSearch(e.target.value);
      }, 500);
    },
    []
  );

  const onDetailsClose = useCallback(() => {
    setDetailsId('');
    setDetailsVisible(false);
  }, []);

  const onDeleteOrganization = useCallback(
    (id: number) => {
      toggleModal('confirm', true, {
        async onConfirm() {
          idToDelete.current = String(id);

          try {
            await deleteOrganization(id);

            if (selected === idToDelete.current) {
              onChangeParent(organizations?.[0]?.value || '');
            }

            refetch();
          } catch (err) {
            showErrorMessage(err);
          } finally {
            idToDelete.current = '';
          }
        }
      });
    },
    [onChangeParent, organizations, refetch, selected, toggleModal]
  );

  return (
    <PageWrapper
      title="Organizations"
      color="white"
      extra={[
        <Private
          key="create-role"
          route={PERMISSION_ROUTES.organization}
          action={PERMISSION_ACTIONS.create}
        >
          <Button type="primary" onClick={() => setDetailsVisible(true)}>
            <PlusOutlined />
            Create organization
          </Button>
        </Private>
      ]}
    >
      <Row>
        <Col sm={8}>
          <Space>
            <Form.Item>
              <Input.Search
                placeholder={t('common.search')}
                onChange={onSearchChange}
              />
            </Form.Item>
          </Space>
        </Col>
      </Row>
      <Table
        dataSource={results}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
          },
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name: Record<string, string>) => name?.en ?? ''
          },
          {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value: number) => dayjs(value).format(FULL_DATE_FORMAT)
          },
          {
            title: '',
            dataIndex: 'id',
            key: 'x',
            width: '5%',
            align: 'right',
            render: (id: number) => (
              <ActionMenu
                data={[
                  {
                    title: t('table.edit'),
                    permission: {
                      route: PERMISSION_ROUTES.organization,
                      action: PERMISSION_ACTIONS.update
                    },
                    action() {
                      setDetailsId(String(id));
                      setDetailsVisible(true);
                    }
                  },
                  {
                    title: 'Settings',
                    permission: {
                      route: PERMISSION_ROUTES.organization,
                      action: PERMISSION_ACTIONS.update
                    },
                    action() {
                      navigate(`settings/${id}/ui`);
                    }
                  },
                  {
                    title: t('table.delete'),
                    permission: {
                      route: PERMISSION_ROUTES.organization,
                      action: PERMISSION_ACTIONS.delete
                    },
                    action: () => onDeleteOrganization(id),
                    menuProps: {
                      style: {
                        color: 'var(--danger-color)'
                      }
                    }
                  }
                ]}
                placement="bottomRight"
                icon={<EllipsisOutlined />}
              />
            )
          }
        ]}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: false
        }}
      />
      {isDetailsVisible && (
        <OrganizationsModal
          onClose={onDetailsClose}
          id={detailsId}
          refetch={refetch}
        />
      )}
    </PageWrapper>
  );
};

export default Organizations;
